import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { ADJUSTMENT_CATEGORIES, errMsg } from "./payrollHelpers";

export default function AddAdjustmentModal({ employeeId, employees, onClose, onSubmit }) {
  const [empId, setEmpId] = useState(employeeId || (employees?.[0]?.id || ""));
  const [category, setCategory] = useState("BONUS");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!empId) return toast.error("Select an employee");
    if (!reason.trim()) return toast.error("A reason is required");
    const amt = parseFloat(amount);
    if (Number.isNaN(amt)) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await onSubmit({ employeeId: empId, category, adjustmentAmount: amt, reason: reason.trim(), notes: notes.trim() });
      onClose();
    } catch (err) {
      toast.error(errMsg(err, "Failed to save adjustment"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200" data-testid="add-adjustment-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900">Add Payroll Adjustment</div>
          <button type="button" onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-3">
          {!employeeId && (
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1">Employee</label>
              <select value={empId} onChange={(e) => setEmpId(e.target.value)} data-testid="adjustment-employee-select" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px] bg-white">
                {(employees || []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="adjustment-category-select" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px] bg-white">
              {ADJUSTMENT_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">
              Amount <span className="text-slate-400 font-normal">(hours for Time; negative for deductions)</span>
            </label>
            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="adjustment-amount-input" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" placeholder="e.g. 50 or -25" />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Reason <span className="text-rose-500">*</span></label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adjustment-reason-input" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" placeholder="e.g. Missed clock-in" />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Notes</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="adjustment-notes-input" className="w-full px-2.5 py-2 rounded-md border border-slate-200 text-[13px] resize-none" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/40">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-md border border-slate-200 text-[13px] font-semibold">Cancel</button>
          <button type="submit" disabled={busy} data-testid="adjustment-submit-btn" className="h-9 px-3 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-[13px] font-semibold">{busy ? "Saving…" : "Save Adjustment"}</button>
        </div>
      </form>
    </div>
  );
}
